import { CsvColumnProfile, CsvProfile } from '../types/csv.types'
import { computeNullStats, isNullLike } from '../utils/nullDetector'
import { detectColumnType } from '../utils/typeDetector'

export interface RawProfileInput {
    fileName: string
    fileSize: number
    delimiter: string
    encoding?: string
    header: string[]
    // Values grouped by column name in insertion order.
    valuesByColumn: Map<string, string[]>
    previewRows: Record<string, string>[]
    totalRows?: number
    duplicateColumnNames: string[]
    warnings: string[]
}

const MAX_SAMPLE_FOR_TYPE = 500

function profileColumn(name: string, index: number, values: readonly string[]): CsvColumnProfile {
    const nullStats = computeNullStats(values)
    const uniqueValues = new Set<string>()
    for (const v of values) {
        if (isNullLike(v)) continue
        uniqueValues.add(v)
    }
    const uniqueCount = uniqueValues.size
    const uniquePercentage = nullStats.nonNullCount === 0 ? 0 : (uniqueCount / nullStats.nonNullCount) * 100
    const isUnique = nullStats.nonNullCount > 0 && uniqueCount === nullStats.nonNullCount

    const sample = values.slice(0, MAX_SAMPLE_FOR_TYPE)
    const detection = detectColumnType(sample)

    return {
        name,
        index,
        detectedType: detection.type,
        nullCount: nullStats.nullCount,
        nonNullCount: nullStats.nonNullCount,
        nullPercentage: nullStats.nullPercentage,
        uniqueCount,
        uniquePercentage,
        isUnique,
        sampleValues: sample.slice(0, 10),
        numberFormat: detection.numberFormat,
        detectedDateFormat: detection.dateFormat,
        isAmbiguousDate: detection.isAmbiguousDate,
    }
}

export function buildCsvProfile(input: RawProfileInput): CsvProfile {
    const columns: CsvColumnProfile[] = input.header.map((name, index) => profileColumn(name, index, input.valuesByColumn.get(name) ?? []))
    return {
        fileName: input.fileName,
        fileSize: input.fileSize,
        delimiter: input.delimiter,
        encoding: input.encoding,
        columns,
        previewRows: input.previewRows,
        totalRows: input.totalRows,
        duplicateColumnNames: input.duplicateColumnNames,
        warnings: input.warnings,
    }
}

export function findDuplicateColumns(header: string[]): string[] {
    const seen = new Map<string, number>()
    const dupes = new Set<string>()
    for (const name of header) {
        const count = (seen.get(name) ?? 0) + 1
        seen.set(name, count)
        if (count > 1) dupes.add(name)
    }
    return [...dupes]
}
